import {
  AfterViewInit,
  Component, HostListener,
  ElementRef,
  Input,
  OnDestroy,
  OnInit,
  QueryList,
  ViewChild,
  ViewChildren
} from '@angular/core';
import {ModalInterface} from '../../modal.interface';
import { Observable, Subject, Subscription} from 'rxjs';
import {ModalStateEvent} from '../../modal-state-event';
import {CurrentUserViewModel} from '../../../../core/models/current-user.view-model';
import {Store} from '@ngrx/store';
import {AppState} from '../../../../state/app.state';
import {LoaderService} from '../../../../core/services/loader.service';
import {PostViewModel} from '../../../timeline/post.view-model';
import {SwiperOptions} from 'swiper/types';
import Swiper, {Keyboard, Mousewheel, Navigation, Pagination, Virtual} from 'swiper';
import {ModalState} from '../../modal-state.enum';
import {MediaViewComponent} from '../../../media-view/media-view.component';
import {VideoService} from '../../../timeline/video.service';
import {PostTypeEnum} from '../../../../core/enums/post-type.enum';
import {EventShowTabsEnum} from '../../../../features/event/event-show/event-show-tabs.enum';
import {SwiperComponent} from "swiper/angular";
import {MediaTypeEnum} from "../../../../core/enums/media-type.enum";
import {ReactionTypeEnum} from "../../../../core/enums/reaction-type.enum";
import {ReactionService} from "../../../../core/services/reaction.service";
import {BottomSheetComponent, BottomSheetDragMetrics} from "../../../components/bottom-sheet/bottom-sheet.component";
import {TruncateOnWordPipe} from "../../../../core/pipes/truncate-on-word.pipe";
import {TranslateService} from "@ngx-translate/core";
import {Router} from "@angular/router";
import {Keyboard as CapKeyboard} from "@capacitor/keyboard";
import {Capacitor, PluginListenerHandle} from "@capacitor/core";
import {QrDialogData} from "../../../qr/qr-dialog.component";
import {PublicResourceUrlService} from "../../../services/public-resource-url.service";
import {OverlayGalleryModalData, OverlayGalleryModalResult} from "./overlay-gallery-modal.interface";
import {MediaOfflineService} from '../../../../core/services/media-offline.service';
import {MediaObjectPipe} from '../../../../core/pipes/media-object.pipe';

Swiper.use([Pagination, Keyboard, Mousewheel, Navigation, Virtual]);


@Component({
  selector: 'app-overlay-gallery',
  templateUrl: './overlay-gallery.component.html',
  styleUrls: ['./overlay-gallery.component.scss'],
})
export class OverlayGalleryComponent implements OnInit, AfterViewInit, OnDestroy, ModalInterface {

  private static nextGallerySession = 0;

  public readonly PostTypeEnum = PostTypeEnum;
  public readonly EventShowTabsEnum = EventShowTabsEnum;

  @Input() public data: OverlayGalleryModalData;
  @ViewChildren(MediaViewComponent, {emitDistinctChangesOnly: false}) mediaVIewComponents: QueryList<MediaViewComponent>;
  @ViewChild('postSwiper') postSwiper: SwiperComponent;
  @ViewChildren('swiperComponent') swiperComponents: QueryList<SwiperComponent>;
  @ViewChild('mobileCaptionText') mobileCaptionText: ElementRef<HTMLElement>;
  @ViewChild('desktopCaptionText') desktopCaptionText: ElementRef<HTMLElement>;
  @ViewChild('bottomSheetComponent') bottomSheetComponent: BottomSheetComponent;

  public currentUser: CurrentUserViewModel | null;
  public state: Observable<ModalStateEvent>;
  public subject: Subject<ModalStateEvent>;
  public currentTime: number = 0;
  public post: PostViewModel;
  public posts: PostViewModel[] = [];
  public config: SwiperOptions;
  public postConfig: SwiperOptions;
  public activeIndex: number;
  public activePostIndex = 0;
  public loaded: boolean = false;
  public windowWidth: number = 0;
  public mediaScale: number = 1;
  public mediaTranslateY: number = 0;
  public mediaStageHeight: number | null = null;
  public isKeyboardOpen: boolean = false;
  public isDescriptionExpanded: boolean = false;
  public showDescriptionToggle: boolean = false;
  public qrDialogVisible = false;
  public qrDialogData: QrDialogData | null = null;
  public isLoadingNextPostPage = false;
  /** True only while the selected video is waiting for real playback readiness. */
  public isActiveVideoLoading = false;
  /** Comments are secondary to the first video frame and mount on demand. */
  public socialActionsActivated = false;
  private reqFrameId: number | null = null;
  private lastDragMetrics: BottomSheetDragMetrics = {progress: 0, translateY: 0, visibleHeight: 0};
  private keyboardShowListener: PluginListenerHandle | null = null;
  private keyboardHideListener: PluginListenerHandle | null = null;
  private readonly mediaIndexesByPost = new Map<number, number>();
  private readonly subscriptions = new Subscription();
  private readonly timeoutIds = new Set<ReturnType<typeof setTimeout>>();
  private activePlaybackKey: string | null = null;
  private activePlaybackVersion = 0;
  /** Adjacent disk prewarm begins only after the selected video has a frame. */
  private adjacentPrewarmPlaybackKey: string | null = null;
  /**
   * One directional decoder is promoted in-place on a swipe. After a swipe it
   * follows the direction back to the slide just left, so a reverse gesture
   * does not rebuild an HLS/MSE pipeline.
   */
  private warmVideoLocation: {postIndex: number; mediaIndex: number} | null = null;
  private warmVideoDirection: -1 | 1 = 1;
  private resourcesReleased = false;
  private advanceAfterSequenceUpdate = false;
  public readonly videoResourceScope = `overlay-gallery-${++OverlayGalleryComponent.nextGallerySession}`;
  private readonly playlistPrewarmScope = `${this.videoResourceScope}:playlists`;
  private readonly playlistPrewarmNeighborCount = 2;

  @HostListener('window:resize', ['$event'])
  onResize(): void {
    this.windowWidth = window.innerWidth;
    this.updateMediaStageHeight();
    this.updateDescriptionToggleVisibility();
  }

  @HostListener('document:keydown', ['$event'])
  public onDocumentKeydown(event: KeyboardEvent): void {
    if (event.defaultPrevented || this.isEditableKeyboardTarget(event.target)) {
      return;
    }

    if (event.key !== 'ArrowUp' && event.key !== 'ArrowDown') {
      return;
    }

    // Consume the key even at the edge of the sequence so the document behind
    // the fixed overlay never scrolls.
    event.preventDefault();
    event.stopPropagation();
    event.stopImmediatePropagation();

    if (event.key === 'ArrowUp') {
      this.previousPost();
      return;
    }

    this.nextPost();
  }

  constructor(
    private store: Store<AppState>,
    private ls: LoaderService,
    public vs: VideoService,
    public reactionService: ReactionService,
    private translate: TranslateService,
    private router: Router,
    private readonly publicResourceUrlService: PublicResourceUrlService,
    private readonly elementRef: ElementRef<HTMLElement>,
    private readonly mediaOffline: MediaOfflineService,
  ) {
  }

  ngOnInit() {
    this.posts = (this.data.posts || [this.data.post]).filter((post) => !!post?.id && this.hasVisibleMedia(post));
    if (!this.posts.length && this.data.post) {
      this.posts = [this.data.post];
    }
    const requestedPostIndex = this.data.postIndex ?? this.posts.findIndex((post) => post.id === this.data.post?.id);
    this.activePostIndex = Math.max(0, Math.min(requestedPostIndex >= 0 ? requestedPostIndex : 0, this.posts.length - 1));
    this.post = this.posts[this.activePostIndex];
    this.currentUser = this.data.currentUser;
    this.activeIndex = this.data.activeIndex;
    this.currentTime = this.data.initialCurrentTime;
    this.isActiveVideoLoading = false;
    this.socialActionsActivated = !this.isActiveMediaVideo();
    this.updateWarmVideoLocation();

    if (this.data.postSequence$) {
      this.subscriptions.add(this.data.postSequence$.subscribe((posts) => {
        this.applyLivePostSequence(posts || []);
      }));
    }

    this.config = {
      slidesPerView: 1,
      // autoHeight: true,
      // virtual: false,
      preloadImages: true,
      // lazy: {
      //   enabled: true,
      //
      // },
      keyboard: {
        enabled: true,
        onlyInViewport: false,
      },
      // navigation: {
      //   enabled: true
      // },
      pagination: true
    };
    this.postConfig = {
      direction: 'vertical',
      slidesPerView: 1,
      nested: true,
      mousewheel: {forceToAxis: true},
      keyboard: false,
      resistanceRatio: 0.55,
      // Keep the sequence and its absolute indexes intact while limiting the
      // actual DOM to the post being watched and the two swipe neighbours.
      virtual: {
        enabled: true,
        addSlidesBefore: 1,
        addSlidesAfter: 1,
      },
    };
    this.syncPostVirtualWindow();
    this.registerKeyboardListeners();
    // this.disableBodyScroll();
  }

  ngAfterViewInit(): void {
    if (typeof requestAnimationFrame !== 'function') {
      return;
    }
    this.reqFrameId = requestAnimationFrame(() => {
      if (this.resourcesReleased) {
        return;
      }
      this.windowWidth = window.innerWidth;
      this.updateMediaStageHeight();
      this.loaded = true;
      this.scheduleTimeout(() => this.updateDescriptionToggleVisibility());
    });
  }

  ngOnDestroy() {
    this.releaseModalResources();
  }

  /**
   * Called by the modal shell before its hide animation. Angular normally
   * destroys this child after that animation, which is too late for HLS and
   * native video memory and is unreliable when a dialog is replaced quickly.
   */
  public releaseModalResources(): void {
    if (this.resourcesReleased) {
      return;
    }

    // Keep local copies while tearing down. The Angular query properties are
    // deliberately cleared afterwards so a delayed PrimeNG hide animation
    // cannot retain this gallery's Swiper and media view trees.
    const mediaViews = this.mediaVIewComponents?.toArray() || [];
    const mediaSwipers = this.swiperComponents?.toArray() || [];
    const postSwiper = this.postSwiper;
    this.resourcesReleased = true;
    this.cancelActiveVideoPlayback();
    this.subscriptions.unsubscribe();
    this.keyboardShowListener?.remove();
    this.keyboardHideListener?.remove();
    this.keyboardShowListener = null;
    this.keyboardHideListener = null;
    mediaSwipers.forEach((swiperComponent) => this.destroySwiper(swiperComponent));
    this.destroySwiper(postSwiper);
    mediaViews.forEach((mediaView) => mediaView.releaseMediaResources());
    this.vs.disposeScope(this.videoResourceScope);
    // A slide can fail to mount while a modal is replaced. In that case the
    // claimed viewport decoder has not been adopted and must not remain in its
    // temporary DOM host.
    const handoffs = this.data?.viewportDecoderHandoffs || (this.data?.viewportDecoderHandoff ? [this.data.viewportDecoderHandoff] : []);
    handoffs.forEach((handoff) => this.vs.disposeViewportDecoderHandoff(handoff));
    this.mediaOffline.clearPrewarmScope(this.playlistPrewarmScope);
    if (this.reqFrameId !== null && typeof cancelAnimationFrame === 'function') {
      cancelAnimationFrame(this.reqFrameId);
    }
    this.reqFrameId = null;
    this.timeoutIds.forEach((timeoutId) => clearTimeout(timeoutId));
    this.timeoutIds.clear();
    this.clearOwnedNativeVideos();
    // These fields can otherwise keep an entire historical sequence reachable
    // while PrimeNG finishes the dialog hide animation.
    this.posts = [];
    this.post = null;
    this.mediaIndexesByPost.clear();
    this.data = null;
    this.detachDestroyedViewReferences();
  }

  close(): void {
    const activeMediaView = this.getActiveMediaView();
    const activePlayer = activeMediaView?.player;
    const sourcePreviewComponent = this.getSourcePreviewComponent();
    const result: OverlayGalleryModalResult = {
      component: this.data.component,
      currentTime: sourcePreviewComponent?.getPlaybackCurrentTime() ?? this.currentTime ?? this.data.initialCurrentTime,
      wasPlaying: !!sourcePreviewComponent?.hasControllablePlayer() && !sourcePreviewComponent.isPlaybackPaused(),
      sourceVideoKey: this.data.sourceVideoKey,
    };

    activePlayer?.pause();
    this.returnActiveViewportDecoder(activeMediaView);
    this.releaseModalResources();
    this.subject.next(new ModalStateEvent(ModalState.SUCCESS, result));
    // this.enableBodyScroll();
  }

  /** A normal close may hand the initially adopted pipeline back to Media Grid. */
  private returnActiveViewportDecoder(activeMediaView: MediaViewComponent | null): void {
    const scope = this.data?.viewportDecoderScope;
    const activeMedia = this.getVisibleMedia(this.posts[this.activePostIndex])[this.activeIndex];
    const resourceKey = this.getVideoResourceKey(this.activePostIndex, this.activeIndex);
    const element = activeMediaView?.getActiveVideoElement();
    if (!activeMediaView || !activeMedia?.id || !element || !scope
      || !this.vs.canReturnViewportWarmDecoder(resourceKey, element, scope, activeMedia.id)) {
      return;
    }
    const transferred = activeMediaView.detachViewportDecoderForReturn();
    if (transferred) {
      this.vs.returnViewportWarmDecoder(resourceKey, transferred.element, scope, activeMedia.id);
    }
  }
  onSwiper(swiper: Swiper, postIndex: number) {
    if (postIndex !== this.activePostIndex) {
      return;
    }
    const currentMediaView = this.getMediaView(postIndex, this.activeIndex);
    if (currentMediaView && (postIndex !== (this.data.postIndex || 0) || this.shouldAutoplayActiveSlideOnOpen())) {
      this.scheduleActiveVideoPlayback();
    }
  }
  onSlideChange(event: [Swiper], postIndex: number) {
    if (postIndex !== this.activePostIndex) {
      return;
    }
    // Playback is activated by MediaView's decoder-readiness gate after the
    // active index input has changed. Keep this callback to pause the slide
    // leaving the viewport, but never issue a competing direct play().
    this.getMediaView(postIndex, event[0].previousIndex)?.player?.pause();

  }


  onNavigationNext(_event: any): void {
  }

  onActiveIndexChange($event: [Swiper], postIndex: number): void {
    if (postIndex !== this.activePostIndex) {
      return;
    }
    const nextMediaIndex = $event[0].activeIndex;
    if (nextMediaIndex !== this.activeIndex) {
      this.warmVideoDirection = nextMediaIndex > this.activeIndex ? -1 : 1;
    }
    this.activeIndex = nextMediaIndex;
    this.currentTime = 0;
    this.isActiveVideoLoading = false;
    this.socialActionsActivated = !this.isActiveMediaVideo();
    this.resetAdjacentVideoPrewarm();
    this.mediaIndexesByPost.set(this.activePostIndex, this.activeIndex);
    this.updateWarmVideoLocation();
    this.syncPostVirtualWindow();
    this.scheduleActiveVideoPlayback();
    this.scheduleTimeout(() => this.updateDescriptionToggleVisibility());
  }

  onPostSlideChange(event: [Swiper]): void {
    const nextPostIndex = event[0].activeIndex;
    if (nextPostIndex === this.activePostIndex || !this.posts[nextPostIndex]) {
      return;
    }

    this.warmVideoDirection = nextPostIndex > this.activePostIndex ? -1 : 1;
    this.mediaVIewComponents?.forEach((mediaView) => mediaView.player?.pause());
    this.activePostIndex = nextPostIndex;
    this.post = this.posts[nextPostIndex];
    this.activeIndex = this.mediaIndexesByPost.get(nextPostIndex) || 0;
    this.currentTime = 0;
    this.isActiveVideoLoading = false;
    this.socialActionsActivated = !this.isActiveMediaVideo();
    this.isDescriptionExpanded = false;
    this.resetAdjacentVideoPrewarm();
    this.updateWarmVideoLocation();
    this.syncPostVirtualWindow();
    this.maybePrefetchNextPostPage();
    this.scheduleActiveVideoPlayback();
    this.scheduleTimeout(() => this.updateDescriptionToggleVisibility());
  }

  onPostReachEnd(): void {
    // Swiper also reports `reachEnd` for a one-item sequence. That is a valid
    // pagination boundary, not a reason to suppress loading the next page.
    if (!this.hasNextPost) {
      if (this.isLoadingNextPostPage) {
        this.refreshPostSwiper(this.posts.length);
        return;
      }
      void this.requestNextPostPage(true, true);
    }
  }

  onPostTouchEnd(event: [Swiper]): void {
    if (event?.[0]?.isEnd && !this.hasNextPost) {
      if (this.isLoadingNextPostPage) {
        this.refreshPostSwiper(this.posts.length);
        return;
      }
      void this.requestNextPostPage(true, true);
    }
  }

  public get hasPreviousPost(): boolean {
    return this.activePostIndex > 0;
  }

  public get hasNextPost(): boolean {
    return this.activePostIndex < this.posts.length - 1;
  }

  public get canLoadNextPostPage(): boolean {
    return !this.isLoadingNextPostPage
      && !!this.data?.loadNextPostPage
      && (this.data.hasNextPostPage ? this.data.hasNextPostPage() : true);
  }

  public get canNavigateNextPost(): boolean {
    return this.hasNextPost || this.canLoadNextPostPage || this.isLoadingNextPostPage;
  }

  public isPostSlideMounted(postIndex: number): boolean {
    if (Math.abs(postIndex - this.activePostIndex) <= 1) {
      return true;
    }
    return this.getVisibleMedia(this.posts[postIndex]).some((media, mediaIndex) =>
      media?.type === MediaTypeEnum.VIDEO && this.getVideoDecoderMode(postIndex, mediaIndex) !== 'inactive'
    );
  }

  public getVideoDecoderMode(postIndex: number, mediaIndex: number): 'active' | 'warm' | 'inactive' {
    const media = this.getVisibleMedia(this.posts[postIndex])[mediaIndex];
    if (media?.type !== MediaTypeEnum.VIDEO) {
      return 'inactive';
    }
    if (postIndex === this.activePostIndex && mediaIndex === this.activeIndex) {
      return 'active';
    }
    // Keep exactly one directional, bounded MSE pipeline. It becomes the
    // active player without a new attachMedia/loadSource when the user goes
    // forward, which is the path responsible for virtually all Reel swipes.
    return this.warmVideoLocation?.postIndex === postIndex
      && this.warmVideoLocation.mediaIndex === mediaIndex
      ? 'warm'
      : 'inactive';
  }

  private updateWarmVideoLocation(): void {
    this.warmVideoLocation = null;
    for (
      let postIndex = this.activePostIndex;
      postIndex >= 0 && postIndex < this.posts.length;
      postIndex += this.warmVideoDirection
    ) {
      const media = this.getVisibleMedia(this.posts[postIndex]);
      const firstMediaIndex = postIndex === this.activePostIndex
        ? this.activeIndex + this.warmVideoDirection
        : this.warmVideoDirection > 0 ? 0 : media.length - 1;
      for (
        let mediaIndex = firstMediaIndex;
        mediaIndex >= 0 && mediaIndex < media.length;
        mediaIndex += this.warmVideoDirection
      ) {
        if (media[mediaIndex]?.type === MediaTypeEnum.VIDEO) {
          this.warmVideoLocation = {postIndex, mediaIndex};
          return;
        }
      }
    }
  }

  public getVideoResourceKey(postIndex: number, mediaIndex: number): string {
    const post = this.posts[postIndex];
    const media = this.getVisibleMedia(this.posts[postIndex])[mediaIndex];
    // Post/media identifiers stay stable when the dynamic sequence appends a
    // page, unlike the transient Swiper indexes used for navigation.
    return `${this.videoResourceScope}:${post?.id || postIndex}:${media?.id || mediaIndex}`;
  }

  /** A handoff is consumed only by the selected active gallery video. */
  public getViewportDecoderHandoff(postIndex: number, mediaIndex: number) {
    const media = this.getVisibleMedia(this.posts[postIndex])[mediaIndex];
    const handoffs = this.data?.viewportDecoderHandoffs || (this.data?.viewportDecoderHandoff ? [this.data.viewportDecoderHandoff] : []);
    return handoffs.find((handoff) => handoff.mediaId === media?.id) || null;
  }

  /** Only the selected slide owns a progressing playback clock. */
  public getSlideCurrentTime(postIndex: number, mediaIndex: number): number {
    return postIndex === this.activePostIndex && mediaIndex === this.activeIndex ? this.currentTime : 0;
  }

  public onSlideCurrentTimeChange(currentTime: number, postIndex: number, mediaIndex: number): void {
    if (postIndex === this.activePostIndex && mediaIndex === this.activeIndex) {
      this.currentTime = currentTime;
    }
  }

  /** Keeps only adjacent startup paths on disk; it never creates extra MSE decoders. */
  private prewarmAdjacentVideoPlaylists(): void {
    if (this.resourcesReleased || !this.posts.length) {
      return;
    }

    const nearestVideo = (direction: -1 | 1): any | null => {
      for (
        let postIndex = this.activePostIndex;
        postIndex >= 0 && postIndex < this.posts.length;
        postIndex += direction
      ) {
        const media = this.getVisibleMedia(this.posts[postIndex]);
        const initialMediaIndex = postIndex === this.activePostIndex
          ? this.activeIndex + direction
          : direction > 0 ? 0 : media.length - 1;
        for (
          let mediaIndex = initialMediaIndex;
          mediaIndex >= 0 && mediaIndex < media.length;
          mediaIndex += direction
        ) {
          if (media[mediaIndex]?.type === MediaTypeEnum.VIDEO) {
            return media[mediaIndex];
          }
        }
      }
      return null;
    };
    const playlistUrls = [nearestVideo(-1), nearestVideo(1)]
      .filter((media): media is any => !!media)
      .map((media) => MediaObjectPipe.transformToLink(media, {formatType: 'm3u8'}))
      .filter((url): url is string => !!url && !url.startsWith('assets/'));

    if (!playlistUrls.length) {
      this.mediaOffline.clearPrewarmScope(this.playlistPrewarmScope);
      return;
    }

    this.mediaOffline.prewarmUrls(playlistUrls, 'playlist', this.playlistPrewarmNeighborCount, {
      scope: this.playlistPrewarmScope,
      cachePlaylistChildren: false,
      startupSegmentCount: 3,
    });
  }

  /** Keep Swiper's normal small DOM window; non-active videos are poster-only. */
  private syncPostVirtualWindow(): void {
    const virtualConfig = this.postConfig?.virtual as {addSlidesBefore?: number; addSlidesAfter?: number} | undefined;
    if (!virtualConfig) {
      return;
    }
    const addSlidesBefore = 1;
    const addSlidesAfter = 1;

    virtualConfig.addSlidesBefore = addSlidesBefore;
    virtualConfig.addSlidesAfter = addSlidesAfter;
    const swiper = this.postSwiper?.swiperRef;
    if (!swiper || swiper.destroyed) {
      return;
    }
    const swiperVirtual = swiper.params.virtual as {addSlidesBefore?: number; addSlidesAfter?: number};
    swiperVirtual.addSlidesBefore = addSlidesBefore;
    swiperVirtual.addSlidesAfter = addSlidesAfter;
    swiper.virtual?.update(true);
    swiper.update();
  }

  public previousPost(): void {
    this.postSwiper?.swiperRef?.slidePrev();
  }

  public nextPost(): void {
    if (this.hasNextPost) {
      this.postSwiper?.swiperRef?.slideNext();
      return;
    }
    if (this.isLoadingNextPostPage) {
      this.refreshPostSwiper(this.posts.length);
      return;
    }
    void this.requestNextPostPage(true, true);
  }

  private isEditableKeyboardTarget(target: EventTarget | null): boolean {
    if (!(target instanceof Element)) {
      return false;
    }

    return !!target.closest('input, textarea, select, [contenteditable=""], [contenteditable="true"], [role="textbox"]');
  }

  private async requestNextPostPage(advanceAfterLoad: boolean, showLoadingSlide: boolean): Promise<void> {
    if (!this.canLoadNextPostPage) {
      return;
    }

    const previousLength = this.posts.length;
    this.isLoadingNextPostPage = true;
    this.advanceAfterSequenceUpdate = this.advanceAfterSequenceUpdate || advanceAfterLoad;
    if (showLoadingSlide) {
      this.refreshPostSwiper(previousLength);
    }
    try {
      const addedPosts = await this.data.loadNextPostPage!();
      if (!addedPosts || this.posts.length <= previousLength) {
        this.advanceAfterSequenceUpdate = false;
      }
    } catch {
      this.advanceAfterSequenceUpdate = false;
    } finally {
      this.isLoadingNextPostPage = false;
      this.refreshPostSwiper();
    }
  }

  private applyLivePostSequence(posts: PostViewModel[]): void {
    const activePostId = this.post?.id;
    const previousLength = this.posts.length;
    const uniquePosts = new Map<number | string, PostViewModel>();
    posts.filter((post) => !!post?.id && this.hasVisibleMedia(post)).forEach((post) => uniquePosts.set(post.id, post));
    const nextPosts = Array.from(uniquePosts.values());
    if (!nextPosts.length) {
      return;
    }

    this.cancelActiveVideoPlayback();
    this.posts = nextPosts;
    const activePostIndex = this.posts.findIndex((post) => post.id === activePostId);
    this.activePostIndex = activePostIndex >= 0
      ? activePostIndex
      : Math.min(this.activePostIndex, this.posts.length - 1);
    this.post = this.posts[this.activePostIndex];
    this.activeIndex = this.mediaIndexesByPost.get(this.activePostIndex) || 0;
    this.isActiveVideoLoading = false;
    this.updateWarmVideoLocation();
    this.syncPostVirtualWindow();

    const shouldAdvance = this.advanceAfterSequenceUpdate && this.posts.length > previousLength;
    this.advanceAfterSequenceUpdate = false;
    this.refreshPostSwiper(shouldAdvance ? previousLength : null);
  }

  private refreshPostSwiper(targetIndex: number | null = null): void {
    this.scheduleTimeout(() => {
      const swiper = this.postSwiper?.swiperRef;
      if (!swiper || swiper.destroyed) {
        return;
      }
      swiper.virtual?.update(true);
      swiper.update();
      if (targetIndex !== null && (targetIndex < this.posts.length || this.isLoadingNextPostPage)) {
        swiper.slideTo(targetIndex);
      } else if (targetIndex === null && swiper.activeIndex >= this.posts.length) {
        swiper.slideTo(Math.max(0, this.posts.length - 1));
      }
    });
  }

  public getVisibleMedia(post: PostViewModel | null | undefined): any[] {
    const media = post?.media || [];
    return this.data.visibleMediaType
      ? media.filter((item) => item?.type === this.data.visibleMediaType)
      : media;
  }

  private hasVisibleMedia(post: PostViewModel | null | undefined): boolean {
    return this.getVisibleMedia(post).length > 0;
  }

  private maybePrefetchNextPostPage(): void {
    const pageSize = this.data.postPageSize;
    const thresholdIndex = this.data.prefetchAtPostIndex;
    if (!pageSize || thresholdIndex === undefined || thresholdIndex < 0) {
      return;
    }
    if (this.activePostIndex % pageSize !== thresholdIndex) {
      return;
    }
    void this.requestNextPostPage(false, false);
  }

  private scheduleActiveVideoPlayback(): void {
    const resourceKey = this.getVideoResourceKey(this.activePostIndex, this.activeIndex);
    if (!this.isActiveMediaVideo() || !resourceKey) {
      this.cancelActiveVideoPlayback();
      this.isActiveVideoLoading = false;
      return;
    }
    // The native video node exists before optional Plyr controls finish
    // loading. Do not make the first HLS/MSE play request wait for that UI
    // module: MediaView safely falls back to native `video.play()`.
    if (!this.getActiveMediaView()) {
      return;
    }
    if (this.activePlaybackKey === resourceKey) {
      return;
    }
    this.cancelActiveVideoPlayback();
    const playbackVersion = ++this.activePlaybackVersion;
    this.activePlaybackKey = resourceKey;
    if (
      this.resourcesReleased
      || playbackVersion !== this.activePlaybackVersion
      || resourceKey !== this.getVideoResourceKey(this.activePostIndex, this.activeIndex)
    ) {
      return;
    }
    this.getActiveMediaView()?.activateLightboxPlayback();
    // Comment data should already be available when the user opens the sheet,
    // but it must not run before the active video's HLS/MSE play attempt.
    // At this point `play()` has been requested on the exact active element.
    this.socialActionsActivated = true;
  }

  private cancelActiveVideoPlayback(): void {
    this.activePlaybackVersion += 1;
    this.activePlaybackKey = null;
  }

  public onLightboxPlayerReady(postIndex: number, mediaIndex: number): void {
    if (postIndex === this.activePostIndex && mediaIndex === this.activeIndex) {
      this.scheduleActiveVideoPlayback();
    }
  }

  public onVideoLoadingChange(event: {key: string | null; loading: boolean}, postIndex: number, mediaIndex: number): void {
    if (
      postIndex === this.activePostIndex
      && mediaIndex === this.activeIndex
      && event.key === this.getVideoResourceKey(postIndex, mediaIndex)
    ) {
      this.isActiveVideoLoading = event.loading;
      if (!event.loading && event.key !== this.adjacentPrewarmPlaybackKey) {
        this.adjacentPrewarmPlaybackKey = event.key;
        this.prewarmAdjacentVideoPlaylists();
      }
    }
  }

  private resetAdjacentVideoPrewarm(): void {
    this.adjacentPrewarmPlaybackKey = null;
    this.mediaOffline.clearPrewarmScope(this.playlistPrewarmScope);
  }

  /** Opens the already-prefetched comment sheet on explicit intent. */
  public openComments(): void {
    this.socialActionsActivated = true;
    this.scheduleTimeout(() => this.bottomSheetComponent?.toggleBottomSheet());
  }

  private isActiveMediaVideo(): boolean {
    return this.getVisibleMedia(this.posts[this.activePostIndex])[this.activeIndex]?.type === MediaTypeEnum.VIDEO;
  }

  public toggleDescriptionExpanded(): void {
    this.isDescriptionExpanded = !this.isDescriptionExpanded;
    this.scheduleTimeout(() => this.updateDescriptionToggleVisibility());
  }

  toggleVideoPlay(): void {
    const mediaView = this.getActiveMediaView();
    if (!mediaView?.player) {
      return;
    }
    if (mediaView.isPlaybackPaused()) {
      this.cancelActiveVideoPlayback();
      this.activePlaybackKey = this.getVideoResourceKey(this.activePostIndex, this.activeIndex);
      mediaView.activateLightboxPlayback();
    } else {
      mediaView.togglePlay({value: false});
    }
  }

  onUnfoldedThreadList(isUnfolded: boolean, bottomSheetComponent: BottomSheetComponent) {
    if (isUnfolded) {
      void bottomSheetComponent.moveToTop();
      return;
    }

    void bottomSheetComponent.moveToMiddle();
  }

  onBottomSheetDragProgress(progress: number): void {
    const normalized = Math.min(1, Math.max(0, progress));
    const collapseProgress = Math.min(1, Math.max(0, (normalized - 0.18) / 0.62));
    this.mediaScale = 1;
    this.mediaTranslateY = -6 * collapseProgress;
  }

  onBottomSheetDragMetrics(metrics: BottomSheetDragMetrics): void {
    this.lastDragMetrics = metrics || this.lastDragMetrics;
    this.updateMediaStageHeight();
  }

  private updateMediaStageHeight(): void {
    const viewportHeight = window.visualViewport?.height || window.innerHeight || 0;
    const reservedBottom = this.getReservedBottomHeight(viewportHeight);
    const stageHeight = Math.max(120, viewportHeight - reservedBottom);
    this.mediaStageHeight = stageHeight;
    this.isKeyboardOpen = this.getKeyboardHeight() > 0;
  }

  private getReservedBottomHeight(viewportHeight: number): number {
    const paneEl = document.querySelector('.pane') as HTMLElement | null;
    if (!paneEl) {
      return Math.max(0, this.lastDragMetrics?.visibleHeight || 0);
    }

    const paneRect = paneEl.getBoundingClientRect();
    const paneTop = Math.max(0, paneRect.top);
    return Math.max(0, viewportHeight - paneTop);
  }

  private registerKeyboardListeners(): void {
    if (Capacitor.getPlatform() === 'web') {
      return;
    }

    CapKeyboard.addListener('keyboardWillShow', () => {
      this.scheduleTimeout(() => this.updateMediaStageHeight());
    }).then((listener) => {
      if (this.resourcesReleased) {
        listener.remove();
        return;
      }
      this.keyboardShowListener = listener;
    });

    CapKeyboard.addListener('keyboardWillHide', () => {
      this.scheduleTimeout(() => this.updateMediaStageHeight());
    }).then((listener) => {
      if (this.resourcesReleased) {
        listener.remove();
        return;
      }
      this.keyboardHideListener = listener;
    });
  }

  private getSourcePreviewComponent(): MediaViewComponent | null {
    return this.getMediaView(this.data.postIndex || 0, this.data.activeIndex) || null;
  }

  private scheduleTimeout(callback: () => void, delayMs: number = 0): void {
    const timeoutId = setTimeout(() => {
      this.timeoutIds.delete(timeoutId);
      if (!this.resourcesReleased) {
        callback();
      }
    }, delayMs);
    this.timeoutIds.add(timeoutId);
  }

  private clearOwnedNativeVideos(): void {
    const host = this.elementRef.nativeElement as HTMLElement | null;
    const videos = host?.querySelectorAll?.('video') || [];
    videos.forEach((video: HTMLVideoElement) => {
      try {
        video.pause();
        video.autoplay = false;
        video.removeAttribute('src');
        video.src = '';
        video.srcObject = null;
        video.load();
      } catch {
        // A detached native media element can reject load() during teardown.
      }
    });
  }

  /**
   * This runs only after the gallery session has closed. It must never be
   * reused for mounted slides because their Swipers are responsible for the
   * active video's autoplay and the neighbouring swipe window.
   */
  private destroySwiper(swiperComponent: SwiperComponent | null | undefined): void {
    const swiper = swiperComponent?.swiperRef;
    if (!swiper) {
      return;
    }

    try {
      swiper.virtual?.removeAllSlides();
    } catch {
      // Virtual slides may already be gone after an Angular view teardown.
    }
    try {
      swiper.detachEvents();
    } catch {
      // Event listeners may already be detached by Swiper.destroy().
    }
    try {
      swiper.destroy(true, true);
    } catch {
      // A nested Swiper can already be destroyed by Angular while the dialog
      // finishes hiding. The remaining cleanup must still continue.
    } finally {
      // SwiperComponent itself can remain reachable until Angular disposes the
      // dynamic view, so do not leave it pointing at a destroyed Swiper.
      (swiperComponent as any).swiperRef = null;
    }
  }

  private detachDestroyedViewReferences(): void {
    this.mediaVIewComponents = null;
    this.swiperComponents = null;
    this.postSwiper = null;
    this.mobileCaptionText = null;
    this.desktopCaptionText = null;
    this.config = null;
    this.postConfig = null;
    this.qrDialogData = null;
    this.qrDialogVisible = false;
  }

  public getMediaIndex(postIndex: number): number {
    return postIndex === this.activePostIndex ? this.activeIndex : (this.mediaIndexesByPost.get(postIndex) || 0);
  }

  public getActiveMediaView(): MediaViewComponent | null {
    return this.getMediaView(this.activePostIndex, this.activeIndex);
  }

  private getMediaView(postIndex: number, mediaIndex: number): MediaViewComponent | null {
    const mediaId = this.getVisibleMedia(this.posts[postIndex])[mediaIndex]?.id;
    if (!mediaId) {
      return null;
    }
    return this.mediaVIewComponents?.find((mediaView) => mediaView.mediaObject?.id === mediaId) || null;
  }

  private shouldAutoplayActiveSlideOnOpen(): boolean {
    if (!this.isTimelineVideoHandoff()) {
      return true;
    }

    return this.data.initialWasPlaying;
  }

  private isTimelineVideoHandoff(): boolean {
    return !!this.data?.component && !!this.data?.sourceVideoKey;
  }

  private getKeyboardHeight(): number {
    const value = getComputedStyle(document.documentElement).getPropertyValue('--keyboard-height').trim();
    const parsed = Number.parseFloat(value);
    return Number.isFinite(parsed) ? parsed : 0;
  }

  private updateDescriptionToggleVisibility(): void {
    const textElement = this.windowWidth < 768
      ? this.mobileCaptionText?.nativeElement
      : this.desktopCaptionText?.nativeElement;

    if (!textElement) {
      this.showDescriptionToggle = false;
      return;
    }

    if (this.isDescriptionExpanded) {
      this.showDescriptionToggle = true;
      return;
    }

    this.showDescriptionToggle = (textElement.scrollHeight - textElement.clientHeight) > 2;
  }

  public openQrDialog(): void {
    this.qrDialogData = {
      resourceType: 'post',
      title: this.resolveShareTitle(),
      subtitle: TruncateOnWordPipe.truncateOnWord(this.post.content, 255, '...'),
      url: this.getPostUrl(),
    };
    this.qrDialogVisible = true;
  }

  private resolveShareTitle(): string {
    if ((this.post.type === PostTypeEnum.PROJECT_POST || this.post.type === PostTypeEnum.PROJECT_MEDIA_POST) && this.projectTargetLabel) {
      return this.projectTargetLabel;
    }
    if (this.post.type === PostTypeEnum.COLLABORATION_MEDIA_POST) {
      return this.post.collaboration?.title
        || this.post.collaboration?.project?.displayVehicleIdentity
        || this.post.collaboration?.project?.vehicle?.fullVehicleIdentity
        || this.post.postedBy?.username;
    }
    return this.post.event?.name || this.post.postedBy?.username;
  }

  private getPostUrl(): string {
    return this.publicResourceUrlService.getPostUrl(this.post.id);
  }

  public get projectTargetLabel(): string {
    return this.post?.project?.displayVehicleIdentity || '';
  }

  public get collaborationProjectTargetLabel(): string {
    return this.post?.collaboration?.project?.displayVehicleIdentity
      || this.post?.collaboration?.project?.vehicle?.fullVehicleIdentity
      || '';
  }

  public readonly MediaTypeEnum = MediaTypeEnum;
  protected readonly ReactionTypeEnum = ReactionTypeEnum;
}
